import { addDays, dayIdOf, isoDayOfWeek, type DayId } from '../calendar.ts'

/**
 * Los festivos nacionales de España, calculados y no descargados.
 *
 * Existen aquí por una pregunta concreta: ¿el `.ics` del calendario escolar
 * trae la Navidad, o solo los días propios del colegio? La descripción oficial
 * del conjunto de datos dice «fiestas autonómicas, provinciales, locales y
 * otras», y ese «otras» no promete nada. En vez de adivinarlo, la app lo
 * comprueba al importar y lo dice.
 *
 * Y se calculan en lugar de pedirlos a una API por la misma razón que el
 * fichero se lee en la tablet: esta app funciona en modo avión, y un aviso que
 * depende de que haya red no es un aviso. Nueve de los diez son fechas fijas;
 * el décimo es el Viernes Santo, que se saca del cómputo de la Pascua.
 *
 * Deliberadamente **solo los nacionales**. Los autonómicos, provinciales y
 * locales sí los trae el fichero de la Junta, y esta app no sabe en qué
 * comunidad vive quien la usa. Lo que hace esto es cubrir el hueco que podría
 * haber, no sustituir al calendario.
 */

export interface Festivo {
  readonly day: DayId
  readonly name: string
}

/** Las nueve que caen siempre en la misma fecha. */
const FIJOS: readonly { readonly month: number; readonly day: number; readonly name: string }[] = [
  { month: 1, day: 1, name: 'Año Nuevo' },
  { month: 1, day: 6, name: 'Reyes' },
  { month: 5, day: 1, name: 'Fiesta del Trabajo' },
  { month: 8, day: 15, name: 'La Asunción' },
  { month: 10, day: 12, name: 'Fiesta Nacional' },
  { month: 11, day: 1, name: 'Todos los Santos' },
  { month: 12, day: 6, name: 'La Constitución' },
  { month: 12, day: 8, name: 'La Inmaculada' },
  { month: 12, day: 25, name: 'Navidad' },
]

/**
 * El domingo de Pascua, por el cómputo gregoriano anónimo. Es aritmética pura
 * sobre el año: ni reloj, ni `Date`, ni zona horaria que pueda corromperlo.
 */
export function easterSunday(year: number): DayId {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return dayIdOf(year, month, day)
}

/**
 * Cuando un festivo cae en domingo se traslada al lunes.
 *
 * No es un detalle: en 2026 el Día de Todos los Santos y el de la Constitución
 * caen los dos en domingo, y el calendario del colegio los trae como «lunes
 * siguiente al...». Sin esto, la comprobación avisaría de dos festivos que no
 * faltan, y un aviso que da falsos positivos se deja de leer enseguida.
 */
function trasladarSiDomingo(day: DayId): DayId {
  return isoDayOfWeek(day) === 7 ? addDays(day, 1) : day
}

/** Los diez festivos nacionales de un año, en orden y ya trasladados. */
export function nationalHolidays(year: number): readonly Festivo[] {
  const festivos: Festivo[] = FIJOS.map((f) => ({
    day: trasladarSiDomingo(dayIdOf(year, f.month, f.day)),
    name: f.name,
  }))
  // Viernes Santo: dos días antes de la Pascua. Es el único nacional que se
  // mueve de año en año, y el único que nunca hay que trasladar, porque por
  // definición es viernes. El Jueves Santo no es nacional —lo es en casi todas
  // las comunidades, Andalucía entre ellas— y por eso no está aquí.
  festivos.push({ day: addDays(easterSunday(year), -2), name: 'Viernes Santo' })
  return [...festivos].sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0))
}

/**
 * Los festivos nacionales que el fichero importado deja sin cubrir.
 *
 * Las dos reglas de abajo salieron de pasar esto por un calendario real —el del
 * CEIP de Mijas, curso 2026/27— y son la diferencia entre un aviso útil y uno
 * que nadie lee:
 *
 * **Solo dentro de lo que abarca el fichero.** Ese calendario va del 12 de
 * octubre al 26 de marzo, porque un curso escolar no es un año natural. Avisar
 * de que «falta el 15 de agosto» en un fichero que acaba en marzo son once
 * avisos falsos de doce, y a la segunda vez nadie los mira.
 *
 * **Solo los que caen en día de colegio.** Un festivo en sábado no hay que
 * declararlo: el fin de semana ya tiene su propio presupuesto. El 1 de mayo de
 * 2027 cae en sábado, y el fichero no lo trae por eso mismo.
 */
export function missingHolidays(
  rangos: readonly { readonly from: string; readonly to: string }[],
): readonly Festivo[] {
  if (rangos.length === 0) return []

  const desde = rangos.reduce((min, r) => (r.from < min ? r.from : min), rangos[0]?.from ?? '')
  const hasta = rangos.reduce((max, r) => (r.to > max ? r.to : max), rangos[0]?.to ?? '')
  if (desde === '' || hasta === '') return []

  const años = new Set([Number(desde.slice(0, 4)), Number(hasta.slice(0, 4))])
  const candidatos = [...años].flatMap((año) => nationalHolidays(año))

  return candidatos.filter((festivo) => {
    if (festivo.day < desde || festivo.day > hasta) return false
    if (isoDayOfWeek(festivo.day) >= 6) return false
    return !rangos.some((r) => r.from <= festivo.day && festivo.day <= r.to)
  })
}
